import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import toastr from "toastr";
import { authFetch } from "../utils/authFetch";
import { usePerm } from "../hooks/usePerm";

/* ---------- helpers ---------- */
const toast = (m, t = "success") => {
  toastr.options = { closeButton: true, progressBar: true, timeOut: 3000 };
  toastr[t](m);
};
export const API_BASE = process.env.REACT_APP_API_BASE || "https://backend.suvidhape.in";
// export const API_BASE = process.env.REACT_APP_API_BASE || "https://backend.macustore.com";
export const toUrl = (path) =>
  path ? (path.startsWith("http") ? path : `${API_BASE}${path}`) : null;
// export const API_BASE = "http://127.0.0.1:6060";

// universal “multipart” sender (text‑only या 1‑file वाले कामों के लिए)
// const sendForm = async (payload, method = "POST") => {
//   const fd = new FormData();

//   /* ⇣⇣  सबसे पहले दोनों ज़रूरी keys डालो  ⇣⇣ */
//   fd.append("id", String(payload.id));
//   fd.append("actiontype", payload.actiontype);

//   /*  अब loop में इन्हें skip कर दो  */
//   Object.entries(payload).forEach(([k, v]) => {
//     if (k === "id" || k === "actiontype") return;  // ⬅️ duplicates रोक दिए
//     if (v == null) return;
//     if (typeof v === "string" && v.trim() === "") return;
//     fd.append(k, v);
//   });

//   /* डिबग */
//   for (const [k, v] of fd) console.log("fd →", k, v);

//   const res = await authFetch(
//     "http://127.0.0.1:6060/api/v1/member/profileupdate",
//     { method, body: fd }
//   );
//   const out = await res.json();
//   if (!res.ok) throw new Error(out.detail || out.status || "Failed");
//   return out;
// };
export const sendForm = async (payload, method = "POST") => {
  /* 1️⃣  FormData तैयार करो */
  const fd = new FormData();

  // जरूरी keys पहले (duplicate एक बार)
  fd.append("id", String(payload.id));
  fd.append("actiontype", payload.actiontype);

  // बाकी सब keys / Files
  Object.entries(payload).forEach(([k, v]) => {
    if (k === "id" || k === "actiontype") return;
    if (v == null) return;
    if (typeof v === "string" && v.trim() === "") return;
    fd.append(k, v);
  });

  /* 2️⃣  DEBUG – payload check */
  for (const [k, v] of fd) console.log("fd →", k, v);

  /* 3️⃣  Plain fetch call  */
  const token = localStorage.getItem("token");     // अगर use करते हो तो
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const res = await fetch(
    // "https://backend.macustore.com/api/v1/member/profileupdate",
    "https://backend.suvidhape.in/api/v1/member/profileupdate",
    { method, headers, body: fd }   // कोई Content‑Type मत लगाओ
  );

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.status || `HTTP ${res.status}`);
  return data;
};


/* ---------- component ---------- */
export default function ViewProfile() {
  const { id } = useParams();
  const { hasPerm, version } = usePerm();

  const [info,        setInfo]    = useState(null);
  const [profileForm, setProfile] = useState(null);      // text + profilepic
  const [kycForm,     setKyc]     = useState(null);
  const [docsForm,    setDocs]    = useState([]);        // ⬅️ array of {tag,file}
  const [pwdForm,     setPwd]     = useState({ old: "", pwd: "", pwd2: "" });
  const [saving,      setSaving]  = useState(false);

  const [permVersion, setPermVersion] = useState(0);
    useEffect(() => {
      const handler = () => setPermVersion(v => v + 1);
      window.addEventListener("permissions-updated", handler);
      return () => window.removeEventListener("permissions-updated", handler);
    }, []);

  /* ---------- fetch once ---------- */
  useEffect(() => {
    (async () => {
      const r  = await authFetch(`/api/v1/member/profile/${id}`);
      const d  = await r.json();

      // convert all backend‑relative paths to absolute URLs once here
      if (d.user) {
        d.user.profilepic = toUrl(d.user.profilepic);
        d.user.documents = (d.user.documents || []).map((doc) =>
          typeof doc === "string" ? toUrl(doc) : { ...doc, url: toUrl(doc.url) }
        );
      }

      setInfo(d);

      const u = d.user;
      setProfile({
        name:     u.name     ?? "",
        email:    u.email    ?? "",
        mobile:   u.mobile   ?? "",
        state:    u.state    ?? "",
        city:     u.city     ?? "",
        address:  u.address  ?? "",
        pincode:  u.pincode  ?? "",
        profilepic: null
      });
      setKyc({
        shopname:   u.shopname   ?? "",
        gstin:      u.gstin      ?? "",
        pancard:    u.pancard    ?? "",
        aadharcard: u.aadharcard ?? ""
      });
    })();
  }, [id]);

  if (!info || !profileForm || !kycForm) return <div className="p-3">Loading…</div>;
  const { user } = info;

  /* ---------- generic handlers ---------- */
  const onText = (setter) => (e) =>
    setter(f => ({ ...f, [e.target.name]: e.target.value }));

  const onFileProfile = (e) => {
    const f = e.target.files[0];
    if (f) setProfile(p => ({ ...p, profilepic: f }));
  };

  // —— multiple‑docs handler
  const onFileDocs = (tag) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDocs(arr => {
      const idx = arr.findIndex(it => it.tag === tag);
      if (idx > -1) {
        const copy = [...arr];
        copy[idx] = { tag, file };
        return copy;
      }
      return [...arr, { tag, file }];
    });
  };

  /* ---------- submit handlers ---------- */

  // Profile (text + single profilepic)
  const submitProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await sendForm({ id, actiontype: "profile", ...profileForm });
      toast("Profile updated ✔");

      if (profileForm.profilepic) {
        setInfo(p => ({
          ...p,
          user: { ...p.user, profilepic: URL.createObjectURL(profileForm.profilepic) }
        }));
        setProfile(f => ({ ...f, profilepic: null }));
      }
    } catch (err) { toast(err.message, "error"); }
      finally      { setSaving(false); }
  };

  // KYC (text only)
  const submitKyc = async (e) => {
    e.preventDefault();
    try { setSaving(true);
          await sendForm({ id, actiontype: "kyc", ...kycForm });
          toast("KYC updated ✔");
    } catch (err) { toast(err.message, "error"); }
      finally      { setSaving(false); }
  };

  // Documents (multi‑files + tags)
  const submitDocs = async (e) => {
    e.preventDefault();

    /* safety: कुछ चुना भी है? */
    if (!docsForm.length) {
      toast("Select at least one document", "error");
      return;
    }

    setSaving(true);
    try {
      /* 1️⃣  FormData तैयार */
      const fd = new FormData();
      fd.append("id", id);
      fd.append("actiontype", "documents");

      docsForm.forEach(({ tag, file }) => {
        if (!file) return;              // skip empty slot
        fd.append("files", file);       // same key → array on backend
        fd.append("tags",  tag);        // same index order
      });

      /* 2️⃣  DEBUG चाहो तो देख लो */
      // for (const [k,v] of fd) console.log("fd‑docs →", k, v);

      /* 3️⃣  fetch (कोई Content‑Type header मत दो) */
      const res  = await fetch(
        // "https://backend.macustore.com/api/v1/member/profileupdate",
        "https://backend.suvidhape.in/api/v1/member/profileupdate",
        {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: fd,
        }
      );
      const out = await res.json();
      if (out.status !== "success") throw new Error(out.status);

      // merge newly uploaded docs (convert to abs URL!)
      const newDocs = (out.documents || []).map((d) => toUrl(d.url || d));
      setInfo((p) => ({
        ...p,
        user: { ...p.user, documents: [...(p.user.documents || []), ...newDocs] },
      }));


      toast("Docs uploaded ✔");
      setDocs([]);                // state reset (inputs भी clear कर दें)
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // Password
  const submitPwd = async (e) => {
    e.preventDefault();
    if (pwdForm.pwd !== pwdForm.pwd2) { toast("Passwords do not match", "error"); return; }
    try { setSaving(true);
          await sendForm({ id, actiontype: "password",
                           oldpassword: pwdForm.old, password: pwdForm.pwd });
          toast("Password changed ✔");
          setPwd({ old:"", pwd:"", pwd2:"" });
    } catch (err) { toast(err.message, "error"); }
      finally      { setSaving(false); }
  };
  return (
    <>
    <div className="page-wrapper">
        {/*page-content-wrapper*/}
        <div className="page-content-wrapper">
          <div className="page-content">
            {/*breadcrumb*/}
            <div className="page-breadcrumb d-none d-md-flex align-items-center mb-3">
              <div className="breadcrumb-title pe-3">User Profile</div>
              <div className="ps-3">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb mb-0 p-0">
                    <li className="breadcrumb-item"><a href="javascript:;"><i className="bx bx-home-alt" /></a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">User Profile</li>
                  </ol>
                </nav>
              </div>
              
            </div>
            {/*end breadcrumb*/}
            <div className="user-profile-page">
              <div className="card radius-15">
                <div className="card-body">
                  <div className="row">
                    <div className="col-12 col-lg-6 border-right">
                      <div className="d-md-flex align-items-center">
                        <div className="mb-md-0 mb-3">
                          <img
                            src={user.profilepic ? user.profilepic : "/assets/images/avatars/avatar-1.png"}
                            className="rounded-circle shadow"
                            width={130}
                            height={130}
                            alt={user.name}
                          />
                        </div>
                        <div className="ms-md-4 flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <h4 className="mb-0">Name : {user.name}</h4>
                          </div>
                          <p className="mb-0 text-muted"><i className="bx bx-envelope" /> {user.email}</p>
                          <p className="text-primary"><i className="bx bx-phone" /> {user.mobile}</p>
                          
                          {/* <button type="button" className="btn btn-primary">Connect</button>
                          <button type="button" className="btn btn-outline-secondary ml-2">Resume</button> */}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-lg-6">
                      <table className="table table-sm table-borderless mt-md-0 mt-3">
                        <tbody>
                          <tr>
                            <th>Merchant Code:</th>
                            <td>{user.merchantcode}</td>
                          </tr>
                          <tr>
                            <th>Address:</th>
                            <td>{user.address}</td>
                          </tr>
                          <tr>
                            <th>Zipcode:</th>
                            <td>{user.pincode}</td>
                          </tr>
                          <tr>
                            <th>PAN:</th>
                            <td>{user.pancard}</td>
                          </tr>
                          <tr>
                            <th>Aadhar:</th>
                            <td>{user.aadharcard}</td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="mb-3 mb-lg-0 d-none"> 
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-github" /></a>
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-twitter" /></a>
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-facebook" /></a>
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-linkedin" /></a>
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-dribbble" /></a>
                        <a href="javascript:;" className="btn btn-sm btn-link"><i className="bx bxl-stack-overflow" /></a>
                      </div>
                    </div>
                  </div>
                  {/*end row*/}
                  <ul className="nav nav-pills">
                    <li className="nav-item"> <a className="nav-link active" data-bs-toggle="tab" href="#Experience"><span className="p-tab-name">Profile Update</span><i className="bx bx-donate-blood font-24 d-sm-none" /></a>
                    </li>
                    {hasPerm("api_user_profile_kyc") && (
                    <li className="nav-item"> <a className="nav-link" id="profile-tab" data-bs-toggle="tab" href="#Biography"><span className="p-tab-name">KYC Update</span><i className="bx bxs-user-rectangle font-24 d-sm-none" /></a>
                    </li>
                    )}
                    {hasPerm("api_user_profile_document") && (
                    <li className="nav-item"> <a className="nav-link" data-bs-toggle="tab" href="#Edit-Profile"><span className="p-tab-name">Upload Documents</span><i className="bx bx-message-edit font-24 d-sm-none" /></a>
                    </li>
                    )}
                    {hasPerm("api_user_profile_password") && (
                    <li className="nav-item"> <a className="nav-link" data-bs-toggle="tab" href="#t4"><span className="p-tab-name">Password Change</span><i className="bx bx-message-edit font-24 d-sm-none" /></a>
                    </li>
                    )}
                  </ul>
                  <div className="tab-content mt-3">
                    <div className="tab-pane fade show active" id="Experience">
                      <div className="card shadow-none border mb-0 radius-15">
                        <div className="card-body">
                          <div className="card-title d-flex align-items-center">
                            <div><i className="bx bxs-user me-1 font-22 text-primary" /></div>
                            <h5 className="mb-0 text-primary">User Profile</h5>
                          </div>
                          <hr />
                          <form className="row g-3 " onSubmit={submitProfile}>
                            {["name","email","mobile","state","city","pincode"].map(f => (
                              <div className="col-md-4" key={f}>
                                <label className="form-label text-capitalize">{f}</label>
                                <input className="form-control"
                                      name={f} value={profileForm[f]} onChange={onText(setProfile)}/>
                              </div>
                            ))}
                            <div className="col-md-4">
                              <label className="form-label">Profile Pic</label>
                              <input type="file" accept="image/*" className="form-control"
                                    onChange={onFileProfile}/>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Address</label>
                              <textarea rows={2} className="form-control"
                                        name="address" value={profileForm.address}
                                        onChange={onText(setProfile)}/>
                            </div>
                            {hasPerm("api_user_profile_edit") && (
                            <div className="col-12 text-start">
                              <button className="btn btn-primary" disabled={saving}>Save Profile</button>
                            </div>
                            )}
                          </form>
                        </div>
                      </div>
                    </div>
                    <div className="tab-pane fade" id="Biography">
                      <div className="row">
                        <div className="card shadow-none border mb-0 radius-15">
                            <div className="card-body">
                              <div className="card-title d-flex align-items-center">
                                <div><i className="bx bxs-user me-1 font-22 text-primary" /></div>
                                <h5 className="mb-0 text-primary">KYC Details</h5>
                              </div>
                              <hr />
                              <form className="row g-3" onSubmit={submitKyc}>
                                {[
                                  ["shopname","Shop Name"],
                                  ["gstin","GST Number"],
                                  ["pancard","PAN Number"],
                                  ["aadharcard","Aadhar Number"]
                                ].map(([n,l]) => (
                                  <div className="col-md-6" key={n}>
                                    <label className="form-label">{l}</label>
                                    <input className="form-control"
                                          name={n} value={kycForm[n]} onChange={onText(setKyc)}/>
                                  </div>
                                ))}
                                <div className="col-12 text-start">
                                  <button className="btn btn-primary" disabled={saving}>Save KYC</button>
                                </div>
                              </form>
                            </div>
                        </div>
                      </div>
                    </div>
                    <div className="tab-pane fade" id="Edit-Profile">
                      <div className="card shadow-none border mb-0 radius-15">
                        <div className="card-body">
                          <div className="card-title d-flex align-items-center">
                            <div><i className="bx bxs-user me-1 font-22 text-primary" /></div>
                            <h5 className="mb-0 text-primary">Document Upload</h5>
                          </div>
                          <form className="row g-3" onSubmit={submitDocs}>
                            {[
                              ["pancardimg",      "PAN Card"],
                              ["aadhar_front", "Aadhar Front"],
                              ["aadhar_back",  "Aadhar Back"],
                              ["shop_act",     "Shop Act"],
                              // और tag‑label जोड़ना हो तो यहाँ add कर दें
                            ].map(([tag,label]) => (
                              <div className="mb-3" key={tag}>
                                <label className="form-label">{label}</label>
                                <input type="file" accept="image/*,application/pdf"
                                      className="form-control"
                                      onChange={onFileDocs(tag)}/>
                              </div>
                            ))}
                            <div className="col-12 text-start">
                            <button className="btn btn-primary" disabled={saving}>Upload Docs</button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>

                    <div className="tab-pane fade" id="t4">
                      <div className="card shadow-none border mb-0 radius-15">
                        <div className="card-body">
                          <div className="card-title d-flex align-items-center">
                            <div><i className="bx bxs-user me-1 font-22 text-primary" /></div>
                            <h5 className="mb-0 text-primary">Change Password</h5>
                          </div>
                          <form className="row g-3" onSubmit={submitPwd}>
                            {[
                              ["old","Old Password"],
                              ["pwd","New Password"],
                              ["pwd2","Repeat New"]
                            ].map(([n,l]) => (
                              <div className="col-md-4" key={n}>
                                <label className="form-label">{l}</label>
                                <input type="password" className="form-control"
                                      name={n} value={pwdForm[n]} onChange={onText(setPwd)}/>
                              </div>
                            ))}
                            <div className="col-12 text-start">
                              <button className="btn btn-primary" disabled={saving}>Change Password</button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>


                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/*end page-content-wrapper*/}
      </div>
    </>
  );
}
